'use client'

import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react'
import { profileApiService as profileService } from '@/lib/profile-apis'
import { useAuthStore } from '@/stores/auth-store'

interface ProfileContextType {
  userProfile: any
  shopProfile: any
  loading: boolean
  isInitialized: boolean
  updateUserProfile: (data: any) => Promise<any>
  updateShopProfile: (data: any) => Promise<any>
  refreshProfiles: () => Promise<void>
}

const ProfileContext = createContext<ProfileContextType | undefined>(undefined)

export const useProfile = () => {
  const context = useContext(ProfileContext)
  
  if (context === undefined) {
    // Return a safe default instead of throwing an error
    return {
      userProfile: null,
      shopProfile: null,
      loading: false,
      isInitialized: true, // Changed from false to true to allow components to proceed
      updateUserProfile: async () => {
        console.warn('Profile provider not ready - updateUserProfile called before initialization');
      },
      updateShopProfile: async () => {
        console.warn('Profile provider not ready - updateShopProfile called before initialization');
      },
      refreshProfiles: async () => {
        console.warn('Profile provider not ready - refreshProfiles called before initialization');
      }
    }
  }
  return context
}

export const ProfileProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [userProfile, setUserProfile] = useState<any>(null)
  const [shopProfile, setShopProfile] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [isInitialized, setIsInitialized] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [providerReady, setProviderReady] = useState(false)
  const { isAuthenticated, isInitialized: authInitialized } = useAuthStore()

  // Ensure we only run client-side code after mounting
  useEffect(() => {
    setMounted(true)
  }, [])

  const refreshProfiles = useCallback(async () => {
    try {
      // Only run in browser environment
      if (typeof window === 'undefined') return
      
      const [userData, shopData] = await Promise.all([
        profileService.getUserProfile(),
        profileService.getShopProfile()
      ])
      
      setUserProfile(userData)
      setShopProfile(shopData)
    } catch (error) {
      console.error('Failed to refresh profiles:', error)
      // Set default profiles on error
      setUserProfile(null)
      setShopProfile(null)
    }
  }, [])

  useEffect(() => {
    // Only run in browser environment and after mounting
    if (typeof window === 'undefined' || !mounted) {
      setLoading(false)
      setIsInitialized(true)
      setProviderReady(true)
      return
    }

    // Wait for auth to be initialized before proceeding
    if (!authInitialized) {
      return
    }

    // Only load profiles if user is authenticated
    if (!isAuthenticated) {
      setLoading(false)
      setIsInitialized(true)
      setProviderReady(true)
      return
    }

    // Load profiles
    const loadProfiles = async () => {
      try {
        const [userData, shopData] = await Promise.all([
          profileService.getUserProfile(),
          profileService.getShopProfile()
        ])
        
        setUserProfile(userData)
        setShopProfile(shopData)
      } catch (error) {
        console.error('Failed to load profiles:', error)
        // Set default profiles on error
        setUserProfile(null)
        setShopProfile(null)
      } finally {
        setLoading(false)
        setIsInitialized(true)
        setProviderReady(true)
      }
    }

    loadProfiles().catch(error => {
      console.error('Profile initialization failed:', error)
      // Ensure we still set ready state even on error
      setLoading(false)
      setIsInitialized(true)
      setProviderReady(true)
    })
  }, [mounted, isAuthenticated, authInitialized]) // Add auth dependencies

  const updateUserProfile = useCallback(async (data: any) => {
    if (!providerReady) {
      throw new Error('Profile provider not ready')
    }
    
    setLoading(true)
    try {
      const updatedProfile = await profileService.updateUserProfile(data)
      setUserProfile(updatedProfile)
      return updatedProfile
    } finally {
      setLoading(false)
    }
  }, [providerReady])

  const updateShopProfile = useCallback(async (data: any) => {
    if (!providerReady) {
      throw new Error('Profile provider not ready')
    }
    
    setLoading(true)
    try {
      const updatedProfile = await profileService.updateShopProfile(data)
      setShopProfile(updatedProfile)
      return updatedProfile
    } finally {
      setLoading(false)
    }
  }, [providerReady])

  // Memoize the context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    userProfile,
    shopProfile,
    loading,
    isInitialized,
    updateUserProfile,
    updateShopProfile,
    refreshProfiles
  }), [userProfile, shopProfile, loading, isInitialized, updateUserProfile, updateShopProfile, refreshProfiles])

  // Always render children to prevent hydration mismatch
  return (
    <ProfileContext.Provider value={value}>
      {children}
    </ProfileContext.Provider>
  )
}
